/**
 * GuideContext / GuideProvider — the seam that carries the guide brain
 * (useGuide, T2) from the authenticated shell down to the render site.
 *
 * Plan: .plan/features/2026-07-02-onboarding-guided-first-recipe.md.
 *
 * The provider mounts near the top of the authenticated shell (routes/app.tsx),
 * instantiating the guide once so the completion RPC + funnel logs are wired and
 * cross-device suppression works from the first beat. The GuideStrip render site
 * lands in a later track (T4) inside the Workspace and reads the guide via
 * `useGuideContext()` — no prop-drilling through the Workspace tree.
 *
 * `useGuideContext()` returns `null` outside a provider (e.g. standalone tests /
 * the marketing shell) rather than throwing: the guide is a non-critical overlay
 * and a missing provider must degrade to "no guide", never to a crash.
 */

import { createContext, useContext, type ReactNode } from "react";
import { useGuide, type GuideState, type UseGuideOptions } from "./useGuide";

const GuideContext = createContext<GuideState | null>(null);

export interface GuideProviderProps extends UseGuideOptions {
  children: ReactNode;
}

/**
 * Instantiates the guide brain and provides it to the subtree. Mount once in the
 * authenticated shell. Passing `onboardingCompletedAt` / `onComplete` straight
 * through to {@link useGuide}.
 */
export function GuideProvider({ onboardingCompletedAt, onComplete, children }: GuideProviderProps) {
  const guide = useGuide({
    onboardingCompletedAt,
    ...(onComplete !== undefined ? { onComplete } : {}),
  });
  return <GuideContext.Provider value={guide}>{children}</GuideContext.Provider>;
}

/**
 * Read the guide state. Returns `null` when no {@link GuideProvider} is mounted
 * above — the guide degrades to absent rather than throwing.
 */
export function useGuideContext(): GuideState | null {
  return useContext(GuideContext);
}
